import React from 'react';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import type { DemoDomain } from '../../data/demoCenter';
import { DomainIcon } from './DomainIcon';
import { DemoStatusBadge } from './DemoStatusBadge';
type DomainRowProps = {
  domain: DemoDomain;
  highlighted: boolean;
  onExplore: (domain: DemoDomain) => void;
};
const focusRing = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal';
const primaryAction = `inline-flex items-center justify-center gap-2 rounded-md bg-navy px-5 py-3 font-semibold text-white hover:bg-navy-light transition-colors duration-150 whitespace-nowrap ${focusRing}`;

/** One domain as a scannable row: who it's for, what it does, and how to open it. */
export function DomainRow({
  domain,
  highlighted,
  onExplore
}: DomainRowProps) {
  const titleId = `${domain.id}-title`;
  const opensExternally = domain.status !== 'planned' && Boolean(domain.demoUrl);
  const href = domain.status !== 'planned' ? domain.demoUrl ?? domain.demoPath : undefined;
  return <article id={domain.id} aria-labelledby={titleId} className={`scroll-mt-24 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 items-center py-6 px-4 -mx-4 rounded-lg transition-[background-color,box-shadow] duration-200 ${highlighted ? 'bg-lavender-pale ring-2 ring-lavender/30' : ''}`} data-id="element-428" data-mp="3b65cym7bw65i">
      <div className="lg:col-span-4 flex items-start gap-4" data-id="element-429" data-mp="3u3q2zwltxarp">
        <DomainIcon id={domain.id} data-id="element-430" data-mp="3u36kadocuicm" />
        <div className="min-w-0" data-id="element-431" data-mp="3q24v6po5jmod">
          <div className="flex flex-wrap items-center gap-2" data-id="element-432" data-mp="3q1v3mxngl7gk">
            <h3 id={titleId} className="font-heading font-bold text-lg text-ink-dark leading-snug" data-id="element-433" data-mp="3l31nsykp94d8">
              {domain.title}
            </h3>
            {domain.status !== 'interactive' && <DemoStatusBadge status={domain.status} data-id="element-434" data-mp="3g61g6j87btk0" />}
          </div>
          <p className="mt-0.5 text-sm font-medium text-lavender" data-id="element-435" data-mp="29oo322l8t51f">{domain.solutionLabel}</p>
        </div>
      </div>

      <div className="lg:col-span-5 pl-14 lg:pl-0" data-id="element-436" data-mp="24nguw2f6u3u8">
        <p className="text-ink-dark leading-relaxed" data-id="element-437" data-mp="265fmx04hvm3h">{domain.description}</p>
        <p className="mt-1.5 text-sm text-ink-muted" data-id="element-438" data-mp="2b51vgw9doe5y">
          <span className="sr-only" data-id="element-439" data-mp="2b4idxgnn3rsg">Workspaces: </span>
          {domain.workspaces.join(' · ')}
        </p>
      </div>

      <div className="lg:col-span-3 pl-14 lg:pl-0 lg:text-right" data-id="element-440" data-mp="1x7dcxv2s9j42">
        {href ? <a id={`${domain.id}-action`} href={href} {...opensExternally ? {
        target: '_blank',
        rel: 'noopener noreferrer'
      } : {}} className={primaryAction} data-id="element-441" data-mp="222iw4m4swjog">
            {domain.actionLabel}
            {opensExternally ? <ArrowUpRight size={18} aria-hidden="true" data-id="element-442" data-mp="1efswtsju737j" /> : <ArrowRight size={18} aria-hidden="true" data-id="element-443" data-mp="1econyhvffjaj" />}
            {opensExternally && <span className="sr-only" data-id="element-444" data-mp="1igw9k66l8j4j">(opens in a new tab)</span>}
          </a> : <button id={`${domain.id}-action`} type="button" aria-haspopup="dialog" onClick={() => onExplore(domain)} className={`inline-flex items-center justify-center gap-2 rounded-md border border-line bg-white px-5 py-3 font-semibold text-navy hover:bg-surface-muted transition-colors duration-150 whitespace-nowrap ${focusRing}`} data-id="element-445" data-mp="14h3p2slnmz50">
            {domain.actionLabel}
            <ArrowRight size={18} aria-hidden="true" data-id="element-446" data-mp="uszgu0oofvax" />
            <span className="sr-only" data-id="element-447" data-mp="us6k7sjfmm6i">: {domain.title}</span>
          </button>}
      </div>
    </article>;
}